import React, { useEffect, useMemo, useState } from 'react';
import { DiagnosticProcessorTrendResponse, DiagnosticProcessorTrendSummary, DiagnosticTrendApi } from '../services/Ai/DiagnosticTrendService';
import { EmptyState, ErrorIndicator, LoadingIndicator } from './common/LoadingStates';
import './DiagnosticTrendPanel.scss';

interface DiagnosticTrendPanelProps {
    stage: string;
    realm: string;
    startDateTime: Date;
    endDateTime: Date;
    selectedClients: string[];
    selectedProcessors: string[];
}

const MAX_HISTORY_DAYS = 30;
const MAX_VISIBLE_PROCESSORS = 4;
const SERIES_COLORS = ['#a63a50', '#0f766e', '#2563eb', '#d97706'];

const formatScaleValue = (value: number): string => {
    if (value >= 1000) {
        return Intl.NumberFormat('en-US', {
            notation: 'compact',
            maximumFractionDigits: 1,
        }).format(value);
    }

    return value.toLocaleString('en-US');
};

const DiagnosticTrendPanel: React.FC<DiagnosticTrendPanelProps> = ({
    stage,
    realm,
    startDateTime,
    endDateTime,
    selectedClients,
    selectedProcessors,
}) => {
    const [response, setResponse] = useState<DiagnosticProcessorTrendResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const requestScopeKey = useMemo(() => JSON.stringify({
        stage,
        realm,
        start: startDateTime.toISOString(),
        end: endDateTime.toISOString(),
        selectedClients,
        selectedProcessors,
    }), [stage, realm, startDateTime, endDateTime, selectedClients, selectedProcessors]);

    const days = useMemo(() => {
        const elapsedMs = Math.max(endDateTime.getTime() - startDateTime.getTime(), 24 * 60 * 60 * 1000);
        return Math.max(1, Math.min(MAX_HISTORY_DAYS, Math.ceil(elapsedMs / (24 * 60 * 60 * 1000))));
    }, [startDateTime, endDateTime]);

    useEffect(() => {
        let active = true;
        const controller = new AbortController();

        const loadProcessorTrends = async () => {
            setLoading(true);
            setError(null);

            try {
                const trendResponse = await DiagnosticTrendApi.getProcessorTrends(stage, realm, {
                    days,
                    clientIds: sanitizeSelections(selectedClients, 'AllClients'),
                    processors: sanitizeSelections(selectedProcessors, 'AllProcessors'),
                    startTime: startDateTime.toISOString(),
                    endTime: endDateTime.toISOString(),
                });

                if (!active || controller.signal.aborted) {
                    return;
                }

                const normalized = normalizeTrendResponse(trendResponse);
                if (!normalized) {
                    throw new Error('Diagnostic trend API returned an unexpected payload shape');
                }

                setResponse(normalized);
            } catch (err) {
                if (!active || controller.signal.aborted) {
                    return;
                }

                setResponse(null);
                setError(err instanceof Error ? err.message : 'Failed to load diagnostic trends');
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };

        loadProcessorTrends();

        return () => {
            active = false;
            controller.abort();
        };
    }, [requestScopeKey, days, stage, realm]);

    const visibleProcessors = useMemo(
        () => (response?.processors ?? []).slice(0, MAX_VISIBLE_PROCESSORS),
        [response],
    );

    const currentLeader = useMemo(() => {
        const processors = response?.processors ?? [];
        return processors.reduce<DiagnosticProcessorTrendSummary | null>((leader, processor) => {
            if (!leader || processor.lastCount > leader.lastCount) {
                return processor;
            }
            return leader;
        }, null);
    }, [response]);

    const topMover = useMemo(() => {
        const processors = response?.processors ?? [];
        if (processors.length === 0) {
            return null;
        }

        if (response?.topSpikingProcessor) {
            const namedProcessor = processors.find(
                (processor) => processor.processor === response.topSpikingProcessor,
            );
            if (namedProcessor) {
                return namedProcessor;
            }
        }

        return processors.reduce<DiagnosticProcessorTrendSummary | null>((leader, processor) => {
            if (!leader || Math.abs(processor.absoluteChange) > Math.abs(leader.absoluteChange)) {
                return processor;
            }
            return leader;
        }, null);
    }, [response]);

    const chartLabels = useMemo(() => {
        const labels = new Set<string>();
        visibleProcessors.forEach((processor) => {
            processor.dailyCounts.forEach((point) => {
                if (point.date) {
                    labels.add(point.date);
                }
            });
        });

        return Array.from(labels).sort();
    }, [visibleProcessors]);

    const chartMax = useMemo(() => {
        const values = visibleProcessors.reduce<number[]>((allCounts, processor) => {
            processor.dailyCounts.forEach((point) => {
                allCounts.push(point.count);
            });
            return allCounts;
        }, []);
        return Math.max(1, ...values, 0);
    }, [visibleProcessors]);

    return (
        <section className="diagnostic-trend-panel">
            <div className="diagnostic-trend-panel__header">
                <div>
                    <h3>Processor backlog trend</h3>
                </div>
                {response && (
                    <div className="diagnostic-trend-panel__meta">
                        <span>{formatWindow(response.windowStart, response.windowEnd)}</span>
                    </div>
                )}
            </div>

            {loading && <LoadingIndicator message="Loading diagnostic trends..." />}
            {!loading && error && <ErrorIndicator error={error} />}

            {!loading && !error && response && response.processors.length === 0 && (
                <EmptyState
                    title="Diagnostic history is building"
                    message="Processor backlog trend data for this window is still building. Refresh again shortly."
                />
            )}

            {!loading && !error && response && response.processors.length > 0 && (
                <>
                    <div className="diagnostic-trend-panel__summary-grid">
                        <SummaryCard
                            label="Top mover"
                            value={topMover?.processor || 'Unknown'}
                            detail={topMover ? formatDelta(topMover.absoluteChange) : 'No change'}
                        />
                        <SummaryCard
                            label="Largest backlog now"
                            value={currentLeader?.processor || 'Unknown'}
                            detail={currentLeader ? `${currentLeader.lastCount.toLocaleString('en-US')} refunds` : 'No data'}
                        />
                        <SummaryCard
                            label="Window"
                            value={`${response.daysRequested} day${response.daysRequested === 1 ? '' : 's'}`}
                            detail={formatWindow(response.windowStart, response.windowEnd)}
                        />
                    </div>

                    <div className="diagnostic-trend-panel__body">
                        <TrendChart labels={chartLabels} processors={visibleProcessors} maxValue={chartMax} />

                        <div className="diagnostic-trend-panel__leaderboard">
                            <h4>Biggest processor moves</h4>
                            <div className="diagnostic-trend-panel__leaderboard-list">
                                {response.processors.slice(0, 6).map((processor, index) => (
                                    <div key={processor.processor} className="leaderboard-row">
                                        <div className="leaderboard-row__identity">
                                            <span
                                                className="leaderboard-row__swatch"
                                                style={{ backgroundColor: SERIES_COLORS[index % SERIES_COLORS.length] }}
                                            />
                                            <div>
                                                <strong>{processor.processor}</strong>
                                                <span>{processor.firstCount.toLocaleString('en-US')} to {processor.lastCount.toLocaleString('en-US')}</span>
                                            </div>
                                        </div>
                                        <div className="leaderboard-row__metrics">
                                            <span className={processor.absoluteChange > 0 ? 'up' : processor.absoluteChange < 0 ? 'down' : 'flat'}>
                                                {formatDelta(processor.absoluteChange)}
                                            </span>
                                            <span>Peak {processor.peakCount.toLocaleString('en-US')} {processor.peakDate ? `on ${processor.peakDate}` : ''}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </section>
    );
};

const SummaryCard: React.FC<{ label: string; value: string; detail: string }> = ({ label, value, detail }) => (
    <div className="diagnostic-trend-summary-card">
        <span className="diagnostic-trend-summary-card__label">{label}</span>
        <strong className="diagnostic-trend-summary-card__value">{value}</strong>
        <span className="diagnostic-trend-summary-card__detail">{detail}</span>
    </div>
);

const TrendChart: React.FC<{
    labels: string[];
    processors: DiagnosticProcessorTrendSummary[];
    maxValue: number;
}> = ({ labels, processors, maxValue }) => {
    const width = 820;
    const height = 280;
    const paddingX = 52;
    const paddingY = 24;
    const tickIndexes = buildTickIndexes(labels.length);
    const yTickRatios = [0, 0.25, 0.5, 0.75, 1];
    const scaleMidpoint = Math.round(maxValue / 2);

    return (
        <div className="diagnostic-trend-chart-card">
                <div className="diagnostic-trend-chart-card__copy">
                <div>
                            <h4>Processor backlog</h4>
                            <p>Backlog movement across the selected window.</p>
                        </div>
                <div className="diagnostic-trend-chart-card__legend">
                    {processors.map((processor, index) => (
                        <span key={processor.processor} className="legend-chip">
                            <span
                                className="legend-swatch"
                                style={{ backgroundColor: SERIES_COLORS[index % SERIES_COLORS.length] }}
                            />
                            {processor.processor}
                        </span>
                    ))}
                </div>
            </div>

            <div className="chart-scale" aria-label="Diagnostic processor trend scale">
                <span className="chart-scale__label">Scale</span>
                <div className="chart-scale__values">
                    <span>Max {formatScaleValue(maxValue)}</span>
                    <span>Mid {formatScaleValue(scaleMidpoint)}</span>
                    <span>Min 0</span>
                </div>
            </div>

            <div className="diagnostic-trend-chart-card__frame">
                <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Diagnostic processor trend chart">
                    <text className="chart-axis-label" x="18" y={height / 2} transform={`rotate(-90 18 ${height / 2})`}>
                        Refund count
                    </text>
                    <g className="chart-grid">
                        {yTickRatios.map((ratio) => {
                            const y = paddingY + (height - paddingY * 2) * ratio;
                            return <line key={ratio} x1={paddingX} y1={y} x2={width - paddingX} y2={y} />;
                        })}
                    </g>
                    {yTickRatios.map((ratio) => {
                        const y = paddingY + (height - paddingY * 2) * ratio;
                        const tickValue = Math.round(maxValue * (1 - ratio));

                        return (
                            <g key={`y-${ratio}`} className="chart-y-tick">
                                <line x1={paddingX - 6} y1={y} x2={paddingX} y2={y} />
                                <text x={paddingX - 10} y={y + 4} textAnchor="end">
                                    {formatScaleValue(tickValue)}
                                </text>
                            </g>
                        );
                    })}
                    {processors.map((processor, index) => (
                        <path
                            key={processor.processor}
                            d={buildLinePath(labels, processor, maxValue, width, height, paddingX, paddingY)}
                            fill="none"
                            stroke={SERIES_COLORS[index % SERIES_COLORS.length]}
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    ))}
                    {tickIndexes.map((index) => {
                        const denominator = Math.max(labels.length - 1, 1);
                        const x = paddingX + ((width - paddingX * 2) * index) / denominator;
                        return (
                            <g key={index} className="chart-tick">
                                <line x1={x} y1={height - paddingY + 4} x2={x} y2={height - paddingY + 10} />
                                <text x={x} y={height - 2} textAnchor={index === 0 ? 'start' : index === labels.length - 1 ? 'end' : 'middle'}>
                                    {formatAxisLabel(labels[index])}
                                </text>
                            </g>
                        );
                    })}
                </svg>
            </div>
        </div>
    );
};

function sanitizeSelections(values: string[], allMarker: string): string[] | undefined {
    const filtered = values.filter((value) => value && value !== allMarker);
    return filtered.length > 0 ? filtered : undefined;
}

function normalizeTrendResponse(payload: unknown): DiagnosticProcessorTrendResponse | null {
    const unwrapped = unwrapTrendPayload(payload);
    if (!unwrapped || typeof unwrapped !== 'object') {
        return null;
    }

    const candidate = unwrapped as Record<string, unknown>;
    if (!Array.isArray(candidate.processors)) {
        return null;
    }

    const processors = candidate.processors
        .map(normalizeProcessorSummary)
        .filter((processor): processor is DiagnosticProcessorTrendSummary => Boolean(processor));

    return {
        generatedAt: typeof candidate.generatedAt === 'string' ? candidate.generatedAt : new Date().toISOString(),
        windowStart: typeof candidate.windowStart === 'string' ? candidate.windowStart : null,
        windowEnd: typeof candidate.windowEnd === 'string' ? candidate.windowEnd : null,
        daysRequested: parseNumber(candidate.daysRequested),
        snapshotsAnalyzed: parseNumber(candidate.snapshotsAnalyzed),
        topSpikingProcessor: typeof candidate.topSpikingProcessor === 'string' ? candidate.topSpikingProcessor : null,
        processors,
    };
}

function unwrapTrendPayload(payload: unknown): unknown {
    if (typeof payload === 'string') {
        try {
            return unwrapTrendPayload(JSON.parse(payload));
        } catch {
            return payload;
        }
    }

    if (!payload || typeof payload !== 'object') {
        return payload;
    }

    const candidate = payload as Record<string, unknown>;
    if (Array.isArray(candidate.processors)) {
        return candidate;
    }

    if ('body' in candidate) {
        return unwrapTrendPayload(candidate.body);
    }

    if ('data' in candidate && Object.keys(candidate).length === 1) {
        return unwrapTrendPayload(candidate.data);
    }

    return candidate;
}

function normalizeProcessorSummary(value: unknown): DiagnosticProcessorTrendSummary | null {
    if (!value || typeof value !== 'object') {
        return null;
    }

    const candidate = value as Record<string, unknown>;
    if (typeof candidate.processor !== 'string' || !candidate.processor.trim()) {
        return null;
    }

    return {
        processor: candidate.processor,
        firstCount: parseNumber(candidate.firstCount),
        lastCount: parseNumber(candidate.lastCount),
        absoluteChange: parseNumber(candidate.absoluteChange),
        percentageChange: typeof candidate.percentageChange === 'number' ? candidate.percentageChange : null,
        peakCount: parseNumber(candidate.peakCount),
        peakDate: typeof candidate.peakDate === 'string' ? candidate.peakDate : null,
        dailyCounts: Array.isArray(candidate.dailyCounts)
            ? candidate.dailyCounts
                .map((point) => {
                    if (!point || typeof point !== 'object') {
                        return null;
                    }

                    const pointCandidate = point as Record<string, unknown>;
                    if (typeof pointCandidate.date !== 'string') {
                        return null;
                    }

                    return {
                        date: pointCandidate.date,
                        count: parseNumber(pointCandidate.count),
                    };
                })
                .filter((point): point is { date: string; count: number } => Boolean(point))
            : [],
    };
}

function parseNumber(value: unknown): number {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }

    if (typeof value === 'string') {
        const parsedValue = Number(value);
        return Number.isFinite(parsedValue) ? parsedValue : 0;
    }

    return 0;
}

function buildLinePath(
    labels: string[],
    processor: DiagnosticProcessorTrendSummary,
    maxValue: number,
    width: number,
    height: number,
    paddingX: number,
    paddingY: number,
): string {
    const countsByLabel = new Map(processor.dailyCounts.map((point) => [point.date, point.count]));
    const innerWidth = width - paddingX * 2;
    const innerHeight = height - paddingY * 2;
    const denominator = Math.max(labels.length - 1, 1);

    return labels.map((label, index) => {
        const count = countsByLabel.get(label) ?? 0;
        const x = paddingX + (innerWidth * index) / denominator;
        const y = paddingY + innerHeight - (innerHeight * count) / Math.max(maxValue, 1);
        return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
    }).join(' ');
}

function buildTickIndexes(pointCount: number): number[] {
    if (pointCount === 0) {
        return [];
    }

    if (pointCount <= 1) {
        return [0];
    }

    const lastIndex = pointCount - 1;
    const tickIndexes = new Set<number>([0, Math.floor(lastIndex / 3), Math.floor((lastIndex * 2) / 3), lastIndex]);
    return Array.from(tickIndexes).sort((left, right) => left - right);
}

function formatAxisLabel(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
    }).format(date);
}

function formatDelta(value: number): string {
    if (value === 0) {
        return 'No change';
    }

    return `${value > 0 ? '+' : ''}${value.toLocaleString('en-US')}`;
}

function formatWindow(windowStart?: string | null, windowEnd?: string | null): string {
    if (windowStart && windowEnd) {
        return `${windowStart} to ${windowEnd}`;
    }

    return 'Current retained window';
}

export default DiagnosticTrendPanel;
